/**
 * SpriteFactory converts palette-indexed PixelFrame data into
 * ImageBitmap-backed Sprite objects ready for canvas drawing.
 */

import type { PixelFrame, SpriteDefinition, Sprite, Palette } from "./SpriteSheet.js";

export class SpriteFactory {
  private cache = new Map<string, Sprite>();

  /** Resolve a palette-indexed PixelFrame to an ImageBitmap. */
  async resolveFrame(frame: PixelFrame, palette: Palette): Promise<ImageBitmap> {
    const canvas = new OffscreenCanvas(frame.width, frame.height);
    const ctx = canvas.getContext("2d")!;
    const imageData = ctx.createImageData(frame.width, frame.height);
    const data = imageData.data;

    for (let i = 0; i < frame.pixels.length; i++) {
      const idx = frame.pixels[i];
      if (idx === 0) continue; // transparent
      const color = palette.get(idx);
      if (!color) continue;
      const off = i * 4;
      data[off] = color[0];
      data[off + 1] = color[1];
      data[off + 2] = color[2];
      data[off + 3] = color[3];
    }

    ctx.putImageData(imageData, 0, 0);
    return createImageBitmap(canvas);
  }

  /** Build a Sprite from a SpriteDefinition + palette. Caches by key. */
  async build(def: SpriteDefinition, palette: Palette, cacheKey?: string): Promise<Sprite> {
    const key = cacheKey ?? def.name;
    const cached = this.cache.get(key);
    if (cached) return cached;

    const frames: ImageBitmap[] = [];
    for (const frame of def.frames) {
      frames.push(await this.resolveFrame(frame, palette));
    }

    const sprite: Sprite = {
      name: def.name,
      frames,
      originX: def.originX,
      originY: def.originY,
      frameCount: frames.length,
      width: def.frames[0].width,
      height: def.frames[0].height,
    };

    this.cache.set(key, sprite);
    return sprite;
  }

  /** Get a previously built sprite by cache key. */
  get(key: string): Sprite | undefined {
    return this.cache.get(key);
  }

  /** Check if a sprite is cached. */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /** Clear all cached sprites. */
  clear(): void {
    for (const sprite of this.cache.values()) {
      for (const frame of sprite.frames) {
        frame.close();
      }
    }
    this.cache.clear();
  }
}
